import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Settings as SettingsIcon, User, Mail, Phone, MapPin, Lock, BookOpen, FileText, Users, Briefcase, Building2, Eye, EyeOff, Globe, CalendarClock, Send, Loader2, Wrench, ShieldCheck, Smartphone, CreditCard, Palette, Bell, ChevronRight, Shield, FileCheck, Receipt, FolderKanban, Menu as MenuIcon, X as XIcon, RefreshCw, Download, Coffee, SlidersHorizontal } from 'lucide-react';
import BreakPolicyCard from '../components/BreakPolicyCard';
import OvertimeDefaultsCard from '../components/OvertimeDefaultsCard';
import PayrollCutoffsCard from '../components/PayrollCutoffsCard';
import StripeConnectCard from '../components/StripeConnectCard';

import TwoFactorAuth from '../components/TwoFactorAuth';
import SiteQrSettingsCard from '../components/SiteQrSettingsCard';
import OvertimePayrollSettings from '../components/OvertimePayrollSettings';
import DaylightSavingCard from '../components/DaylightSavingCard';
import { tzRegion, describeTzSpec } from '../utils/timezone';
import InstallPWA from '../components/InstallPWA';
import ToolboxManager from '../components/ToolboxManager';
import SmartAlerts from '../components/SmartAlerts';
import { MissedClockInSettings } from '../components/MissedClockInSettings';
import BillingDashboard from '../components/BillingDashboard';
import CompanyBranding from '../components/CompanyBranding';
import CustomersManager from '../components/CustomersManager';
import ProjectsManager from '../components/ProjectsManager';
import WorkAreasManager from '../components/WorkAreasManager';
import LanguageSelector from '../components/LanguageSelector';
import NotificationPreferencesDialog from '../components/NotificationPreferencesDialog';
import SafetyAttestationSettings from '../components/SafetyAttestationSettings';
import ArrivalNotificationsSettings from '../components/ArrivalNotificationsSettings';
import InvoiceTemplateSettings from '../components/InvoiceTemplateSettings';
import CompanyDefaultsCard from '../components/CompanyDefaultsCard';
import PayrollRulesCard from '../components/PayrollRulesCard';
import MyPayStatements from '../components/MyPayStatements';
import GeneralWorkMigrationCard from '../components/GeneralWorkMigrationCard';
import RecurringOverviewCard from '../components/RecurringOverviewCard';
import EstimateSettings from '../components/EstimateSettings';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';
import { QbSyncErrors, QbConnectionTest } from '../components/QbSyncTools';
import { PayrollReadinessCheck } from '../components/PayrollReadinessCheck';
import { PayItemsMapping, AutoLockSwitch } from '../components/QbPayrollSettings';
import LandingChoice from '../components/nav/LandingChoice';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

// Industries served — keep in sync with backend models.Industry enum
const INDUSTRY_OPTIONS = [
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'construction', label: 'Construction' },
  { value: 'facilities_mgmt', label: 'Facilities Management' },
  { value: 'property_mgmt', label: 'Property Management' },
  { value: 'moving', label: 'Moving' },
  { value: 'security', label: 'Security' },
  { value: 'pest_control', label: 'Pest Control' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'other', label: 'Other' },
];
const industryLabel = (val) =>
  (INDUSTRY_OPTIONS.find((i) => i.value === val) || {}).label || (val || '—');

const Settings = () => {
  const { user, company, token, updateUser, refreshCompanyData, isSuperUser, isAdmin, isSupervisor, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  const handleDataExport = async () => {
    setExportingData(true);
    try {
      const res = await axios.get(`${API}/company/data-export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hinestime-data-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'Export ready', description: 'Your company data ZIP has been downloaded.' });
    } catch (e) {
      toast({ title: 'Export failed', description: 'Please try again in a moment.', variant: 'destructive' });
    } finally {
      setExportingData(false);
    }
  };
  const [showForceRefreshDialog, setShowForceRefreshDialog] = useState(false);
  const [forceRefreshSaving, setForceRefreshSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [teams, setTeams] = useState([]);
  const [section, setSection] = useState('profile');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Deep-link support: ?section=safety-attestation opens the matching
  // section on mount AND on subsequent in-app navigations (sidebar's
  // promoted Customers / Projects links re-navigate while already here).
  const routerLocation = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const requested = params.get('section');
    if (requested) {
      setSection(requested);
    }
  }, [routerLocation.search]);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    // Structured personal address — separate fields so couriers, tax
    // records, and Hines payroll forms can pull each piece independently.
    unit_number: user?.unit_number || '',
    street_address: user?.street_address || '',
    city: user?.city || '',
    state: user?.state || '',
    postal_code: user?.postal_code || '',
    country: user?.country || '',
    // Legacy single-line address — only used as a one-time fallback when
    // structured fields are empty (handled in the useEffect below).
    address: user?.address || '',
    timezone: tzRegion(user?.timezone || 'America/Toronto'),
    team_id: user?.team_id || ''
  });
  
  const [companyFormData, setCompanyFormData] = useState({
    company_name: company?.company_name || '',
    industry: company?.industry || '',
    street_address: company?.street_address || '',
    city: company?.city || '',
    state: company?.state || '',
    postal_code: company?.postal_code || '',
    country: company?.country || '',
    phone: company?.phone || '',
    email: company?.email || '',
    website: company?.website || '',
    billing_email: company?.billing_email || '',
    billing_contact_name: company?.billing_contact_name || '',
    billing_phone: company?.billing_phone || '',
    billing_address: company?.billing_address || '',
    billing_notes: company?.billing_notes || ''
  });

  // Debug logging for production
  useEffect(() => {
    logger.debug('Settings Page - User:', user);
    logger.debug('Settings Page - Company:', company);
    logger.debug('Settings Page - Token:', token ? 'exists' : 'missing');
    logger.debug('Settings Page - isAdmin:', isAdmin);
    logger.debug('Settings Page - isSupervisor:', isSupervisor);
  }, [user, company, token, isAdmin, isSupervisor]);

  // Update formData when user changes (e.g., after profile update)
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        unit_number: user.unit_number || '',
        street_address: user.street_address || '',
        city: user.city || '',
        state: user.state || '',
        postal_code: user.postal_code || '',
        country: user.country || '',
        // Show legacy single-line address only if structured fields are
        // empty (e.g. user signed up before we split the schema). The
        // backend keeps the legacy field around for backward-compat.
        address: user.address || '',
        timezone: tzRegion(user.timezone || 'America/Toronto'),
        team_id: user.team_id || ''
      });
    }
  }, [user?.id]); // same-user refreshes must not wipe in-progress edits
  
  // Update company formData when company changes
  useEffect(() => {
    if (company) {
      setCompanyFormData({
        company_name: company.company_name || '',
        industry: company.industry || '',
        street_address: company.street_address || '',
        city: company.city || '',
        state: company.state || '',
        postal_code: company.postal_code || '',
        country: company.country || '',
        phone: company.phone || '',
        email: company.email || '',
        website: company.website || '',
        billing_email: company.billing_email || '',
        billing_contact_name: company.billing_contact_name || '',
        billing_phone: company.billing_phone || '',
        billing_address: company.billing_address || '',
        billing_notes: company.billing_notes || ''
      });
    }
  }, [company?.id]); // deliberately NOT [company]: same-company refreshes must not wipe in-progress edits

  // Refresh company data when Settings page loads (important for billing status)
  useEffect(() => {
    if (isAdmin && refreshCompanyData) {
      refreshCompanyData();
    }
  }, [isAdmin, refreshCompanyData]);

  
  // Fetch teams for team selector
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await axios.get(`${API}/teams`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeams(response.data);
      } catch (error) {
        console.error('Failed to fetch teams:', error);
      }
    };
    
    if (token) {
      fetchTeams();
    }
  }, [token]);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'Image size should be less than 2MB',
        variant: 'destructive'
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Error',
        description: 'Please upload an image file',
        variant: 'destructive'
      });
      return;
    }

    setIsUploadingPicture(true);

    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        
        // Update profile picture
        const response = await axios.put(
          `${API}/users/profile`,
          { profile_picture: base64String },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        updateUser(response.data);
        
        toast({
          title: 'Success',
          description: 'Profile picture updated successfully!',
        });
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to upload picture',
        variant: 'destructive'
      });
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.put(`${API}/users/me/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user in context with the returned user data
      if (response.data.user) {
        updateUser(response.data.user);
      }
      setIsEditing(false);
      
      toast({
        title: 'Success',
        description: 'Profile updated successfully!',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update profile',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSaveCompanyProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axios.put(`${API}/company/profile`, companyFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: 'Success',
        description: 'Company information updated successfully! Page will refresh.',
      });
      // Reload AFTER the toast so the user actually sees the confirmation
      setTimeout(() => window.location.reload(), 900);
    } catch (error) {
      console.error('Company update error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update company information',
        variant: 'destructive'
      });
      setIsLoading(false);
    }
  };
  
  // Handle country change to update state/province label
  const handleCountryChange = (country) => {
    setCompanyFormData({
      ...companyFormData,
      country: country,
      state: '' // Reset state when country changes
    });
  };
  
  // Get state/province label based on country
  const getStateLabel = () => {
    if (companyFormData.country === 'Canada') return 'Province';
    if (companyFormData.country === 'United States') return 'State';
    if (companyFormData.country === 'Mexico') return 'State';
    if (['United Kingdom', 'Ireland', 'France', 'Germany', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Portugal', 'Poland', 'Sweden', 'Norway', 'Denmark', 'Finland'].includes(companyFormData.country)) {
      return 'Region/County';
    }
    return 'State/Province/Region';
  };
  
  // Countries list
  const countries = [
    // North America
    'United States',
    'Canada',
    'Mexico',
    // Europe
    'United Kingdom',
    'Ireland',
    'France',
    'Germany',
    'Italy',
    'Spain',
    'Netherlands',
    'Belgium',
    'Switzerland',
    'Austria',
    'Portugal',
    'Poland',
    'Sweden',
    'Norway',
    'Denmark',
    'Finland',
    'Greece',
    'Czech Republic',
    'Hungary',
    'Romania'
  ];

  const handleForceRefreshSessions = async () => {
    setForceRefreshSaving(true);
    try {
      const res = await axios.post(
        `${API}/auth/force-refresh-sessions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newSchema = res?.data?.required_schema;
      if (newSchema) {
        // Apply locally so we don't immediately reload ourselves into a loop
        localStorage.setItem('ht_session_schema', newSchema);
        sessionStorage.setItem('ht_schema_reloaded', newSchema);
      }
      toast({
        title: 'Sessions will refresh',
        description: res?.data?.message || 'All browsers tied to this company will reload on their next page change.',
      });
      setShowForceRefreshDialog(false);
    } catch (err) {
      toast({
        title: 'Failed to bump session schema',
        description: err.response?.data?.detail || err.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setForceRefreshSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast({
        title: 'Error',
        description: 'New passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters long',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password?current_password=${encodeURIComponent(passwordData.current_password)}&new_password=${encodeURIComponent(passwordData.new_password)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: 'Success',
        description: 'Password changed successfully!',
      });
      
      setShowChangePasswordDialog(false);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to change password',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[1][0];
    }
    return names[0][0];
  };

  const getRoleBadge = () => {
    const roleConfig = {
      admin: { label: 'Administrator', color: 'bg-[#1A2433] text-red-800' },
      supervisor: { label: 'Supervisor', color: 'bg-[#1A2433] text-blue-800' },
      user: { label: 'User', color: 'bg-[#1A2433] text-green-800' }
    };
    
    const config = roleConfig[user?.role] || roleConfig.user;
    return (
      <span className={`${config.color} px-3 py-1 rounded-full text-sm font-semibold`}>
        {config.label}
      </span>
    );
  };

  // Not signed in on this origin (e.g. landed here after an external redirect) →
  // go to sign-in and come straight back instead of spinning forever.
  if (!loading && !user) {
    const next = encodeURIComponent(routerLocation.pathname + routerLocation.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }

  // Show loading state while authentication is being verified
  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-400">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A2433] via-[#0F1923] to-[#1A2433] pb-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E88E5] to-[#00C896] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SettingsIcon className="w-8 h-8" />
              <h1 className="text-2xl font-bold">Settings</h1>
            </div>
            {/* Mobile sub-nav toggle */}
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              className="md:hidden p-2 rounded-lg bg-white/10 hover:bg-white/20"
              data-testid="settings-mobile-nav-open"
              aria-label="Open settings menu"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
          </div>
          <p className="text-white/95 font-medium mt-1">Manage your profile and preferences</p>
        </div>
      </div>

      {/* Main: 2-column layout (sub-nav | content) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
        {/* Desktop sub-nav */}
        <aside className="hidden md:block" data-testid="settings-subnav">
          <SettingsSubNav
            active={section}
            onSelect={setSection}
            navigate={navigate}
            isAdmin={isAdmin}
            isSupervisor={isSupervisor}
            isSuperUser={isSuperUser}
          />
        </aside>

        {/* Mobile drawer */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 z-[100] md:hidden"
            onClick={() => setMobileNavOpen(false)}
            data-testid="settings-mobile-nav-backdrop"
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
              className="absolute left-0 top-0 bottom-0 w-72 bg-[#0F1923] border-r border-[#2D3748] p-4 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-semibold">Settings</h2>
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(false)}
                  className="text-slate-400 p-1.5 rounded hover:bg-white/10"
                  aria-label="Close menu"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
              <SettingsSubNav
                active={section}
                onSelect={(s) => { setSection(s); setMobileNavOpen(false); }}
                navigate={(p) => { navigate(p); setMobileNavOpen(false); }}
                isAdmin={isAdmin}
                isSupervisor={isSupervisor}
                isSuperUser={isSuperUser}
              />
            </div>
          </div>
        )}

        {/* Right content column */}
        <div className="min-w-0" data-testid={`settings-section-${section}`}>
        {/* Profile Card */}
        {section === 'profile' && (
        <Card className="shadow-xl border-2 mb-6">
          <CardHeader className="bg-gradient-to-r from-blue-100 to-green-100 border-b">
            <CardTitle className="text-xl font-bold">Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center space-x-6 mb-6">
              <div className="relative">
                <Avatar className="w-24 h-24 bg-gradient-to-br from-[#1A2433]0 to-[#1A2433]0 text-white text-3xl font-bold">
                  {user?.profile_picture ? (
                    <img 
                      src={user.profile_picture} 
                      alt={user.name} 
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <AvatarFallback>{getInitials()}</AvatarFallback>
                  )}
                </Avatar>
                <label 
                  htmlFor="profile-picture-upload" 
                  className="absolute bottom-0 right-0 w-8 h-8 bg-[#1E88E5] rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors shadow-lg"
                >
                  <input
                    id="profile-picture-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                    disabled={isUploadingPicture}
                  />
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </label>
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white">{user?.name}</h2>
                <p className="text-slate-400">{user?.email}</p>
                <div className="mt-2">{getRoleBadge()}</div>
                {isUploadingPicture && (
                  <p className="text-sm text-[#1E88E5] mt-2">Uploading picture...</p>
                )}
              </div>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-11"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+14035551234"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-11"
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="unit_number">Unit / Suite / Apt #</Label>
                    <Input
                      id="unit_number"
                      name="unit_number"
                      placeholder="Apt 4B"
                      autoComplete="address-line2"
                      value={formData.unit_number}
                      onChange={handleChange}
                      className="h-11"
                      data-testid="profile-unit-number-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input
                    id="street_address"
                    name="street_address"
                    placeholder="123 Main Street"
                    autoComplete="address-line1"
                    value={formData.street_address}
                    onChange={handleChange}
                    className="h-11"
                    data-testid="profile-street-input"
                  />
                  {formData.address && !formData.street_address && (
                    <p className="text-xs" style={{ color: '#FFA000' }}>
                      Heads up — your old single-line address was{' '}
                      <span className="font-mono">"{formData.address}"</span>. Split it across the new fields above and save.
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="profile_city">City</Label>
                    <Input
                      id="profile_city"
                      name="city"
                      placeholder="Calgary"
                      autoComplete="address-level2"
                      value={formData.city}
                      onChange={handleChange}
                      className="h-11"
                      data-testid="profile-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile_state">Province / State</Label>
                    <Input
                      id="profile_state"
                      name="state"
                      placeholder={formData.country === 'Canada' ? 'Alberta' : formData.country === 'United States' ? 'Nevada' : 'Province or State'}
                      autoComplete="address-level1"
                      value={formData.state}
                      onChange={handleChange}
                      className="h-11"
                      data-testid="profile-state-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="profile_postal">Postal Code / ZIP</Label>
                    <Input
                      id="profile_postal"
                      name="postal_code"
                      placeholder={formData.country === 'Canada' ? 'T2Z 3R6' : '89501'}
                      autoComplete="postal-code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      className="h-11"
                      data-testid="profile-postal-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile_country">Country</Label>
                    <Select
                      value={formData.country || ''}
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="profile-country-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                        <SelectItem value="United States">🇺🇸 United States</SelectItem>
                        <SelectItem value="United Kingdom">🇬🇧 United Kingdom</SelectItem>
                        <SelectItem value="Australia">🇦🇺 Australia</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Your Timezone</Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(value) => setFormData({ ...formData, timezone: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="profile-timezone-select">
                      <SelectValue placeholder="Select your timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* North America - United States */}
                      <SelectItem value="America/New_York">Eastern Time (EST/EDT)</SelectItem>
                      <SelectItem value="America/Chicago">Central Time (CST/CDT)</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time (MST/MDT)</SelectItem>
                      <SelectItem value="America/Phoenix">Arizona Time (MST - No DST)</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time (PST/PDT)</SelectItem>
                      <SelectItem value="America/Anchorage">Alaska Time (AKST/AKDT)</SelectItem>
                      <SelectItem value="Pacific/Honolulu">Hawaii Time (HST)</SelectItem>
                      
                      {/* North America - Canada */}
                      <SelectItem value="America/Toronto">Canada Eastern (EST/EDT)</SelectItem>
                      <SelectItem value="America/Winnipeg">Canada Central (CST/CDT)</SelectItem>
                      <SelectItem value="America/Regina">Saskatchewan (CST - No DST)</SelectItem>
                      <SelectItem value="America/Edmonton">Canada Mountain (MST/MDT)</SelectItem>
                      <SelectItem value="America/Vancouver">Canada Pacific (PST/PDT)</SelectItem>
                      <SelectItem value="America/Halifax">Canada Atlantic (AST/ADT)</SelectItem>
                      <SelectItem value="America/St_Johns">Newfoundland (NST/NDT)</SelectItem>
                      
                      {/* Europe - Western */}
                      <SelectItem value="Europe/London">UK - London (GMT/BST)</SelectItem>
                      <SelectItem value="Europe/Dublin">Ireland - Dublin (GMT/IST)</SelectItem>
                      <SelectItem value="Europe/Lisbon">Portugal - Lisbon (WET/WEST)</SelectItem>
                      
                      {/* Europe - Central */}
                      <SelectItem value="Europe/Paris">France - Paris (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Berlin">Germany - Berlin (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Rome">Italy - Rome (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Madrid">Spain - Madrid (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Amsterdam">Netherlands - Amsterdam (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Brussels">Belgium - Brussels (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Zurich">Switzerland - Zurich (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Vienna">Austria - Vienna (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Stockholm">Sweden - Stockholm (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Copenhagen">Denmark - Copenhagen (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Oslo">Norway - Oslo (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Warsaw">Poland - Warsaw (CET/CEST)</SelectItem>
                      <SelectItem value="Europe/Prague">Czech Republic - Prague (CET/CEST)</SelectItem>
                      
                      {/* Europe - Eastern */}
                      <SelectItem value="Europe/Athens">Greece - Athens (EET/EEST)</SelectItem>
                      <SelectItem value="Europe/Helsinki">Finland - Helsinki (EET/EEST)</SelectItem>
                      <SelectItem value="Europe/Bucharest">Romania - Bucharest (EET/EEST)</SelectItem>
                      <SelectItem value="Europe/Sofia">Bulgaria - Sofia (EET/EEST)</SelectItem>
                      <SelectItem value="Europe/Kiev">Ukraine - Kyiv (EET/EEST)</SelectItem>
                      
                      {/* Europe - Other */}
                      <SelectItem value="Europe/Moscow">Russia - Moscow (MSK)</SelectItem>
                      <SelectItem value="Europe/Istanbul">Turkey - Istanbul (TRT)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Your timezone is used to display all times correctly in reports and on the dashboard. Update if you work in a different timezone.
                    {user?.timezone && user.timezone.includes('|') && (
                      <span className="block mt-1 text-amber-300" data-testid="profile-dst-note">Daylight saving: {describeTzSpec(user.timezone).split(' · ')[1]} (set by your administrator).</span>
                    )}
                  </p>
                </div>

                
                {/* Team Assignment */}
                <div className="space-y-2">
                  <Label htmlFor="team">Team Assignment</Label>
                  {isEditing ? (
                    <>
                      <Select
                        value={formData.team_id || 'none'}
                        onValueChange={(value) => setFormData({ ...formData, team_id: value === 'none' ? '' : value })}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue placeholder="Select your team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="flex items-center space-x-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span>No Team (Individual)</span>
                            </span>
                          </SelectItem>
                          {teams.map((team) => (
                            <SelectItem key={team.id} value={team.id}>
                              <span className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-[#A78BFA]" />
                                <span>{team.name} - {team.location}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        {isAdmin 
                          ? "Administrators can select which team they're on. You can be both an admin and a team member."
                          : "Your team assignment. Contact your administrator if this needs to be changed."
                        }
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="h-11 px-3 py-2 border border-[#2D3748] rounded-lg bg-[#1A2433] flex items-center">
                        {formData.team_id ? (
                          <span className="flex items-center space-x-2">
                            <Users className="w-4 h-4 text-[#A78BFA]" />
                            <span>{teams.find(t => t.id === formData.team_id)?.name || 'Unknown Team'}</span>
                          </span>
                        ) : (
                          <span className="flex items-center space-x-2 text-gray-500">
                            <User className="w-4 h-4" />
                            <span>No Team (Individual)</span>
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {formData.team_id 
                          ? `You are assigned to this team. ${isAdmin ? 'Click Edit to change.' : 'Contact your administrator to change.'}` 
                          : `You work as an individual. ${isAdmin ? 'Click Edit to join a team.' : ''}`
                        }
                      </p>
                    </>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={isLoading}
                    className="flex-1 bg-[#00C896] hover:bg-green-700 h-11"
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    onClick={() => setIsEditing(false)}
                    variant="outline"
                    className="flex-1 h-11"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-3 bg-[#1A2433] rounded-lg">
                    <Mail className="w-5 h-5 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium break-all">{user?.email || 'Not set'}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-[#1A2433] rounded-lg">
                    <Phone className="w-5 h-5 text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium break-all">{user?.phone || 'Not set'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3 p-3 bg-[#1A2433] rounded-lg">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-gray-500">Address</p>
                    {(() => {
                      // Build the structured single-line summary. Falls back
                      // to legacy `user.address` only when none of the
                      // structured fields are present.
                      const parts = [
                        user?.unit_number,
                        user?.street_address,
                        user?.city,
                        user?.state,
                        user?.postal_code,
                        user?.country,
                      ].filter(Boolean);
                      if (parts.length > 0) {
                        return <p className="text-sm font-medium">{parts.join(', ')}</p>;
                      }
                      return <p className="text-sm font-medium">{user?.address || 'Not set'}</p>;
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-[#1A2433] rounded-lg">
                  <Globe className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-xs text-gray-500">Timezone</p>
                    <p className="text-sm font-medium" data-testid="profile-timezone-display">
                      {user?.timezone ? describeTzSpec(user.timezone) : 'Not set'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setIsEditing(true)}
                  className="w-full bg-[#1E88E5] hover:bg-blue-700 h-11"
                >
                  Edit Profile
                </Button>
                <LandingChoice token={token} user={user} />
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Company Info */}
        {section === 'company' && (
        <Card className="shadow-lg mb-6">
          <CardHeader className="bg-[#1A2433] border-b">
            <CardTitle className="text-lg font-bold flex items-center space-x-2">
              <Building2 className="w-5 h-5" />
              <span>Company Information</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {isSuperUser ? (
              /* Editable Company Form — ONLY the Super User (the person who
                 first registered the company and paid for the subscription)
                 can edit company-level info and billing details. Regular
                 admins manage staff/jobs/etc. but cannot change the company
                 record. Backend enforces this independently. */
              <div className="space-y-4">
                {/* Company logo — managed in Company Branding, surfaced here */}
                <div className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50" data-testid="company-info-logo-block">
                  {company?.company_logo ? (
                    <img
                      src={`${API}/company/logo/${company.company_logo}`}
                      alt="Company logo"
                      className="h-14 w-14 rounded-md object-contain bg-white border border-slate-200"
                      data-testid="company-info-logo-preview"
                    />
                  ) : (
                    <div className="h-14 w-14 rounded-md bg-slate-200 dark:bg-slate-700 flex items-center justify-center" data-testid="company-info-logo-placeholder">
                      <Building2 className="w-6 h-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Company Logo</p>
                    <p className="text-xs text-slate-500">
                      {company?.company_logo ? 'Shown on your customer portal, estimates and emails.' : 'No logo yet — add one so estimates and your customer portal carry your brand.'}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setSection('branding')} data-testid="company-info-manage-logo-btn">
                    {company?.company_logo ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                </div>
                <div>
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={companyFormData.company_name}
                    onChange={(e) => setCompanyFormData({...companyFormData, company_name: e.target.value})}
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="industry">Primary Industry</Label>
                  <Select
                    value={companyFormData.industry || ''}
                    onValueChange={(value) => setCompanyFormData({ ...companyFormData, industry: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="settings-industry-select">
                      <SelectValue placeholder="Select your primary industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_OPTIONS.map((ind) => (
                        <SelectItem
                          key={ind.value}
                          value={ind.value}
                          data-testid={`settings-industry-${ind.value}`}
                        >
                          {ind.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Used for analytics and to tailor templates for your business.
                  </p>
                </div>
                <div>
                  <Label htmlFor="company_phone">Company Phone</Label>
                  <Input
                    id="company_phone"
                    name="company_phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="e.g. +14035551234"
                    value={companyFormData.phone}
                    onChange={(e) => setCompanyFormData({...companyFormData, phone: e.target.value})}
                    className="h-11"
                    data-testid="company-phone-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Click the field and type your number. Format: +1 followed by 10 digits.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_email">Main Email Account</Label>
                    <Input
                      id="company_email"
                      type="email"
                      placeholder="sales@hinesfacilities.com"
                      value={companyFormData.email}
                      onChange={(e) => setCompanyFormData({...companyFormData, email: e.target.value})}
                      className="h-11"
                      data-testid="company-email-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Primary contact email shown to customers</p>
                  </div>
                  <div>
                    <Label htmlFor="company_website">Website</Label>
                    <Input
                      id="company_website"
                      type="url"
                      placeholder="https://hinesfacilities.com"
                      value={companyFormData.website}
                      onChange={(e) => setCompanyFormData({...companyFormData, website: e.target.value})}
                      className="h-11"
                      data-testid="company-website-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input
                    id="street_address"
                    placeholder="123 Main Street"
                    value={companyFormData.street_address}
                    onChange={(e) => setCompanyFormData({...companyFormData, street_address: e.target.value})}
                    className="h-11"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      placeholder="Reno"
                      value={companyFormData.city}
                      onChange={(e) => setCompanyFormData({...companyFormData, city: e.target.value})}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">{getStateLabel()}</Label>
                    <Input
                      id="state"
                      name="state"
                      autoComplete="address-level1"
                      placeholder={companyFormData.country === 'Canada' ? 'e.g. Alberta' : companyFormData.country === 'United States' ? 'e.g. Nevada' : 'Region'}
                      value={companyFormData.state}
                      onChange={(e) => setCompanyFormData({...companyFormData, state: e.target.value})}
                      className="h-11"
                      data-testid="company-state-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Postal Code / ZIP</Label>
                    <Input
                      id="postal_code"
                      placeholder="89501"
                      value={companyFormData.postal_code}
                      onChange={(e) => setCompanyFormData({...companyFormData, postal_code: e.target.value})}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Select
                      value={companyFormData.country}
                      onValueChange={handleCountryChange}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="United States">🇺🇸 United States</SelectItem>
                        <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                        <SelectItem value="Mexico">🇲🇽 Mexico</SelectItem>
                        <SelectItem value="United Kingdom">🇬🇧 United Kingdom</SelectItem>
                        <SelectItem value="Ireland">🇮🇪 Ireland</SelectItem>
                        <SelectItem value="France">🇫🇷 France</SelectItem>
                        <SelectItem value="Germany">🇩🇪 Germany</SelectItem>
                        <SelectItem value="Italy">🇮🇹 Italy</SelectItem>
                        <SelectItem value="Spain">🇪🇸 Spain</SelectItem>
                        <SelectItem value="Netherlands">🇳🇱 Netherlands</SelectItem>
                        <SelectItem value="Belgium">🇧🇪 Belgium</SelectItem>
                        <SelectItem value="Switzerland">🇨🇭 Switzerland</SelectItem>
                        <SelectItem value="Austria">🇦🇹 Austria</SelectItem>
                        <SelectItem value="Portugal">🇵🇹 Portugal</SelectItem>
                        <SelectItem value="Poland">🇵🇱 Poland</SelectItem>
                        <SelectItem value="Sweden">🇸🇪 Sweden</SelectItem>
                        <SelectItem value="Norway">🇳🇴 Norway</SelectItem>
                        <SelectItem value="Denmark">🇩🇰 Denmark</SelectItem>
                        <SelectItem value="Finland">🇫🇮 Finland</SelectItem>
                        <SelectItem value="Greece">🇬🇷 Greece</SelectItem>
                        <SelectItem value="Czech Republic">🇨🇿 Czech Republic</SelectItem>
                        <SelectItem value="Hungary">🇭🇺 Hungary</SelectItem>
                        <SelectItem value="Romania">🇷🇴 Romania</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={handleSaveCompanyProfile}
                  disabled={isLoading}
                  className="w-full bg-[#1E88E5] hover:bg-blue-700 h-11"
                >
                  {isLoading ? 'Saving...' : 'Save Company Information'}
                </Button>
              </div>
            ) : (
              /* Read-only Company Info for non-admin users (Supervisors, Staff, Viewers) */
              <div className="space-y-3" data-testid="company-info-readonly">
                <div className="rounded-md p-3 mb-2" style={{ background: 'rgba(255,160,0,0.10)', border: '1px solid rgba(255,160,0,0.30)' }}>
                  <p className="text-xs text-amber-300">
                    Only company administrators can edit company information.
                    Need a change? Ask an admin.
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Company Name</p>
                  <p className="text-lg font-semibold text-white">{company?.company_name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Primary Industry</p>
                  <p className="text-white">{industryLabel(company?.industry)}</p>
                </div>
                {company?.email && (
                  <div>
                    <p className="text-sm text-gray-400">Main Email</p>
                    <p className="text-white">{company.email}</p>
                  </div>
                )}
                {company?.phone && (
                  <div>
                    <p className="text-sm text-gray-400">Company Phone</p>
                    <p className="text-white">{company.phone}</p>
                  </div>
                )}
                {company?.website && (
                  <div>
                    <p className="text-sm text-gray-400">Website</p>
                    <p className="text-white">
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                        {company.website}
                      </a>
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-400">Address</p>
                  <p className="text-white">
                    {company?.street_address && `${company.street_address}, `}
                    {company?.city && `${company.city}, `}
                    {company?.state && `${company.state} `}
                    {company?.postal_code}
                  </p>
                  <p className="text-white">{company?.country}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Clock-In Verification (Site QR) — admin-only company default */}
        {section === 'company' && isAdmin && (
          <SiteQrSettingsCard token={token} />
        )}

        {section === 'company-defaults' && isAdmin && (
          <CompanyDefaultsCard token={token} />
        )}

        {section === 'payroll-rules' && isAdmin && (
          <PayrollRulesCard token={token} />
        )}

        {section === 'my-pay-statements' && (
          <MyPayStatements />
        )}

        {/* Overtime Alerts + Payroll Deadlines + Exception Thresholds */}
        {section === 'overtime-payroll' && isAdmin && (
          <>
            <OvertimeDefaultsCard token={token} />
            <OvertimePayrollSettings token={token} />
          </>
        )}

        {/* Admin Options */}
        {section === 'hrm' && (isAdmin || isSupervisor) && (
          <Card className="shadow-lg mb-6 border-2 border-[#2D3748]">
            <CardHeader className="bg-[#0F1923] border-b border-[#2D3748]">
              <CardTitle className="text-lg font-bold text-blue-900">Human Resource Management</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              <Button
                onClick={() => navigate('/manage-users')}
                variant="outline"
                className="w-full justify-start h-12 text-left hover:bg-[#0F1923]"
              >
                <Users className="w-5 h-5 mr-3" />
                Manage Users
              </Button>
              {isAdmin && (
                <Button
                  onClick={() => navigate('/manage-teams')}
                  variant="outline"
                  className="w-full justify-start h-12 text-left hover:bg-[#0F1923]"
                >
                  <Briefcase className="w-5 h-5 mr-3" />
                  Manage Teams
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Toolbox Meeting Manager (Admin/Supervisor) */}
        {section === 'toolbox' && (isAdmin || isSupervisor) && (
          <ToolboxManager token={token} isAdmin={isAdmin} isSupervisor={isSupervisor} />
        )}

        {/* Customers (Admin/Supervisor) */}
        {section === 'customers' && (isAdmin || isSupervisor) && (
          <CustomersManager token={token} isAdmin={isAdmin} />
        )}

        {/* Projects (QuickBooks-aligned project numbers) — Admin/Supervisor */}
        {section === 'projects' && isAdmin && (
          <GeneralWorkMigrationCard token={token} />
        )}
        {section === 'projects' && (isAdmin || isSupervisor) && (
          <ProjectsManager token={token} isAdmin={isAdmin} isSupervisor={isSupervisor} />
        )}

        {/* Work Areas — geographic/project labels for the clock-in picker */}
        {section === 'work-areas' && isAdmin && (
          <WorkAreasManager token={token} isAdmin={isAdmin} />
        )}

        {/* Weekly Digest Settings (Admin only) */}
        {section === 'weekly-digest' && isAdmin && (
          <WeeklyDigestCard token={token} company={company} />
        )}

        {/* Customer arrival notifications (Admin/Super-user) */}
        {section === 'arrival-notifications' && isAdmin && (
          <ArrivalNotificationsSettings />
        )}

        {/* Notification preferences (any user) */}
        {section === 'notifications' && (
          <NotificationPrefsLauncher />
        )}

        {/* Meal break policy (Admin) */}
        {section === 'meal-breaks' && isAdmin && (
          <BreakPolicyCard token={token} />
        )}

        {/* Payroll cut-offs (Admin) */}
        {section === 'payroll-cutoffs' && isAdmin && (
          <PayrollCutoffsCard token={token} />
        )}

        {section === 'daylight-saving' && isAdmin && (
          <DaylightSavingCard token={token} />
        )}

        {/* Online payments — Stripe Connect (Admin) */}
        {section === 'payments' && isAdmin && (
          <StripeConnectCard token={token} />
        )}

        {/* QuickBooks Integration (Admin only) */}
        {section === 'quickbooks' && isSuperUser && (
          <QuickBooksCard token={token} />
        )}

        {section === 'invoice-template' && isSuperUser && (
          <InvoiceTemplateSettings token={token} />
        )}

        {section === 'recurring' && isAdmin && (
          <RecurringOverviewCard token={token} />
        )}

        {section === 'estimates' && isAdmin && (
          <EstimateSettings token={token} />
        )}

        {/* Smart Alerts (Admin only) */}
        {section === 'smart-alerts' && isAdmin && (
          <div className="space-y-4">
            <MissedClockInSettings token={token} />
            <SmartAlerts token={token} user={user} />
          </div>
        )}

        {/* Billing & Subscription (Super User only) */}
        {section === 'billing' && isSuperUser && (
          <BillingDashboard company={company} token={token} />
        )}

        {/* Company Branding (Admin and Super User) */}
        {section === 'branding' && (isAdmin || isSuperUser) && (
          <CompanyBranding company={company} token={token} />
        )}

        {/* Two-Factor Authentication */}
        {section === '2fa' && (
          <Card className="shadow-lg">
            <CardHeader className="bg-[#1A2433] border-b">
              <CardTitle className="text-lg font-bold">Two-Factor Authentication</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <TwoFactorAuth user={user} token={token} company={company} />
            </CardContent>
          </Card>
        )}

        {/* Install the HinesTime App */}
        {section === 'install' && (
          <Card className="shadow-lg">
            <CardHeader className="bg-[#1A2433] border-b">
              <CardTitle className="text-lg font-bold">Install the HinesTime App</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <InstallPWA />
            </CardContent>
          </Card>
        )}

        {/* Language Settings */}
        {section === 'language' && (
          <Card className="shadow-lg">
            <CardHeader className="bg-[#1A2433] border-b">
              <CardTitle className="text-lg font-bold">Language Settings</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="border rounded-lg p-4 hover:bg-[#1A2433]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 mr-3 text-slate-400" />
                    <span className="font-medium">Language</span>
                  </div>
                  <LanguageSelector />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Safety Attestation — OH&S / OSHA fit-for-duty checklist editor.
            Admin / super-user only. Workers don't see the editor; they
            see the resulting checklist in the dialog at job start. */}
        {section === 'safety-attestation' && (isAdmin || isSuperUser) && (
          <SafetyAttestationSettings />
        )}

        {/* Security & Compliance — includes Change Password */}
        {section === 'security' && (
          <Card className="shadow-lg">
            <CardHeader className="bg-[#1A2433] border-b">
              <CardTitle className="text-lg font-bold">Security &amp; Compliance</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-2">
              <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start h-12 text-left hover:bg-[#1A2433]"
                    data-testid="security-change-password-btn"
                  >
                    <Lock className="w-5 h-5 mr-3" />
                    Change Password
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-2xl">Change Password</DialogTitle>
                    <DialogDescription>
                      Enter your current password and choose a new one
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleChangePassword} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="current_password">Current Password *</Label>
                      <div className="relative">
                        <Input
                          id="current_password"
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder="Enter current password"
                          value={passwordData.current_password}
                          onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                          required
                          className="h-11 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-slate-300 focus:outline-none"
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new_password">New Password *</Label>
                      <div className="relative">
                        <Input
                          id="new_password"
                          type={showNewPassword ? "text" : "password"}
                          placeholder="Enter new password"
                          value={passwordData.new_password}
                          onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                          required
                          minLength={6}
                          className="h-11 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-slate-300 focus:outline-none"
                        >
                          {showNewPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">Must be at least 6 characters</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm_password">Confirm New Password *</Label>
                      <div className="relative">
                        <Input
                          id="confirm_password"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm new password"
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                          required
                          className="h-11 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-slate-300 focus:outline-none"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex space-x-3 pt-4">
                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="flex-1 bg-[#1E88E5] hover:bg-blue-700 h-11"
                      >
                        {isLoading ? 'Changing...' : 'Change Password'}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          setShowChangePasswordDialog(false);
                          setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
                        }}
                        variant="outline"
                        className="flex-1 h-11"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Button
                onClick={() => navigate('/security')}
                variant="outline"
                className="w-full justify-start h-12 text-left hover:bg-[#1A2433]"
                data-testid="security-compliance-page-btn"
              >
                <Shield className="w-5 h-5 mr-3" />
                View Security &amp; Compliance Details
              </Button>
              <Button
                onClick={() => navigate('/terms')}
                variant="outline"
                className="w-full justify-start h-12 text-left hover:bg-[#1A2433]"
              >
                <FileText className="w-5 h-5 mr-3" />
                Terms of Service
              </Button>
              <Button
                onClick={() => navigate('/privacy')}
                variant="outline"
                className="w-full justify-start h-12 text-left hover:bg-[#1A2433]"
              >
                <FileText className="w-5 h-5 mr-3" />
                Privacy Policy
              </Button>

              {/* Super-User-only: GDPR company data export (Aug 2026 Europe launch) */}
              {isSuperUser && (
                <div className="pt-3 mt-3 border-t border-[#2D3748]" data-testid="security-data-export-section">
                  <p className="text-xs text-slate-400 mb-2 px-1">
                    Download a ZIP of your company&apos;s data (users, jobs, time entries, customers, and more) — covers GDPR data-portability requests. Passwords and secrets are never included.
                  </p>
                  <Button
                    onClick={handleDataExport}
                    disabled={exportingData}
                    variant="outline"
                    className="w-full justify-start h-12 text-left hover:bg-[#1A2433]"
                    data-testid="security-data-export-btn"
                  >
                    <Download className="w-5 h-5 mr-3" />
                    {exportingData ? 'Preparing export…' : 'Export Company Data (GDPR)'}
                  </Button>
                </div>
              )}

              {/* Super-User-only: force-refresh all sessions for this company */}
              {isSuperUser && (
                <div className="pt-3 mt-3 border-t border-[#2D3748]" data-testid="security-force-refresh-section">
                  <p className="text-xs text-amber-300/90 mb-2 px-1">
                    <strong>Super User actions.</strong> Use these only when you need an immediate global change — e.g., after a permission update or security incident.
                  </p>
                  <Dialog open={showForceRefreshDialog} onOpenChange={setShowForceRefreshDialog}>
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start h-12 text-left border-amber-500/40 hover:bg-amber-950/30 hover:border-amber-500"
                        data-testid="security-force-refresh-btn"
                      >
                        <RefreshCw className="w-5 h-5 mr-3 text-amber-400" />
                        Force-refresh all sessions
                        <span className="ml-auto text-xs text-slate-400 font-normal">all browsers</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md" data-testid="force-refresh-dialog">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <RefreshCw className="w-5 h-5 text-amber-400" />
                          Force-refresh all sessions
                        </DialogTitle>
                        <DialogDescription>
                          Every browser currently signed in to {company?.company_name || 'your company'} will reload on its next page change and re-fetch the latest user + company data from the server.
                          Users will <strong>not be signed out</strong>; their tokens stay valid.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="rounded-md p-3 my-2 text-sm" style={{ background: 'rgba(255,160,0,0.10)', border: '1px solid rgba(255,160,0,0.30)' }}>
                        Use this when you've just changed someone's role, fixed a permission, or need every device to drop its cached settings immediately.
                      </div>
                      <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowForceRefreshDialog(false)} disabled={forceRefreshSaving} data-testid="force-refresh-cancel-btn">
                          Cancel
                        </Button>
                        <Button
                          onClick={handleForceRefreshSessions}
                          disabled={forceRefreshSaving}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="force-refresh-confirm-btn"
                        >
                          {forceRefreshSaving ? (
                            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Bumping schema…</>
                          ) : (
                            <><RefreshCw className="w-4 h-4 mr-2" />Force refresh now</>
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        </div>
      </div>


    </div>
  );
};

export default Settings;

// ---------- Settings sub-navigation ----------
function SettingsSubNav({ active, onSelect, navigate, isAdmin, isSupervisor, isSuperUser }) {
  // Items with `section` are in-page panes; items with `path` navigate away.
  const items = [
    { key: 'profile', label: 'Profile Information', icon: User, section: 'profile', group: 'My Settings' },
    { key: 'my-pay-statements', label: 'My Pay Statements', icon: FileText, section: 'my-pay-statements', group: 'My Settings' },
    { key: '2fa', label: 'Two-Factor Authentication', icon: ShieldCheck, section: '2fa', group: 'My Settings' },
    { key: 'notifications', label: 'Notifications', icon: Bell, section: 'notifications', group: 'My Settings' },
    { key: 'language', label: 'Language Settings', icon: Globe, section: 'language', group: 'My Settings' },
    { key: 'install', label: 'Install the HinesTime App', icon: Smartphone, section: 'install', group: 'My Settings' },
    { key: 'company', label: 'Company Information', icon: Building2, section: 'company', group: 'Company' },
    { key: 'company-defaults', label: 'Company Defaults', icon: SlidersHorizontal, section: 'company-defaults', show: isAdmin, group: 'Company' },
    { key: 'branding', label: 'Company Branding', icon: Palette, section: 'branding', show: isAdmin || isSuperUser, group: 'Company' },
    { key: 'customers', label: 'Customers', icon: Briefcase, section: 'customers', show: isAdmin || isSupervisor, group: 'Company' },
    { key: 'projects', label: 'Projects', icon: FolderKanban, section: 'projects', show: isAdmin || isSupervisor, group: 'Company' },
    { key: 'work-areas', label: 'Work Areas (Clock-in)', icon: MapPin, section: 'work-areas', show: isAdmin, group: 'Company' },
    { key: 'meal-breaks', label: 'Meal Breaks', icon: Coffee, section: 'meal-breaks', show: isAdmin, group: 'Company' },
    { key: 'payroll-cutoffs', label: 'Payroll Cut-offs', icon: CalendarClock, section: 'payroll-cutoffs', show: isAdmin, group: 'Company' },
    { key: 'daylight-saving', label: 'Time Zone & Daylight Saving', icon: Globe, section: 'daylight-saving', show: isAdmin, group: 'Company' },
    { key: 'payments', label: 'Online Payments', icon: CreditCard, section: 'payments', show: isAdmin, group: 'Company' },
    { key: 'hrm', label: 'Human Resource Management', icon: Users, section: 'hrm', show: isAdmin || isSupervisor, group: 'Company' },
    { key: 'toolbox', label: 'Toolbox Meetings', icon: Wrench, section: 'toolbox', show: isAdmin || isSupervisor, group: 'Company' },
    { key: 'safety-attestation', label: 'Safety Attestation (OH&S)', icon: ShieldCheck, section: 'safety-attestation', show: isAdmin || isSuperUser, group: 'Company' },
    { key: 'smart-alerts', label: 'Smart Alerts', icon: Bell, section: 'smart-alerts', show: isAdmin, group: 'Company' },
    { key: 'overtime-payroll', label: 'Overtime & Payroll Deadlines', icon: CalendarClock, section: 'overtime-payroll', show: isAdmin, group: 'Company' },
    { key: 'payroll-rules', label: 'Payroll Rules (Pay Runs)', icon: CalendarClock, section: 'payroll-rules', show: isAdmin, group: 'Company' },
    { key: 'arrival-notifications', label: 'Customer Notifications', icon: Bell, section: 'arrival-notifications', show: isAdmin, group: 'Company' },
    { key: 'weekly-digest', label: 'Weekly Email Digest', icon: CalendarClock, section: 'weekly-digest', show: isAdmin, group: 'Company' },
    { key: 'quickbooks', label: 'QuickBooks Online', icon: FileCheck, section: 'quickbooks', show: isSuperUser, group: 'Billing & Integrations' },
    { key: 'invoice-template', label: 'Invoice Template (PDF)', icon: Receipt, section: 'invoice-template', show: isSuperUser, group: 'Billing & Integrations' },
    { key: 'recurring', label: 'Recurring & Scheduled', icon: RefreshCw, section: 'recurring', show: isAdmin, group: 'Billing & Integrations' },
    { key: 'estimates', label: 'Estimates', icon: FileText, section: 'estimates', show: isAdmin, group: 'Billing & Integrations' },
    { key: 'billing', label: 'Billing & Subscription', icon: CreditCard, section: 'billing', show: isSuperUser, group: 'Billing & Integrations' },
    { key: 'user-manual', label: 'User Manual', icon: BookOpen, path: '/user-manual', group: 'Help & Legal' },
    { key: 'security', label: 'Security & Compliance', icon: Shield, section: 'security', group: 'Help & Legal' },
    { key: 'terms', label: 'Terms of Service', icon: FileText, path: '/terms', group: 'Help & Legal' },
    { key: 'privacy', label: 'Privacy Policy', icon: FileText, path: '/privacy', group: 'Help & Legal' },
  ].filter((it) => it.show !== false);

  const groups = [];
  items.forEach((it) => {
    const g = groups.find((x) => x.name === it.group);
    if (g) g.items.push(it);
    else groups.push({ name: it.group, items: [it] });
  });

  return (
    <nav
      className="rounded-xl overflow-hidden md:sticky md:top-4"
      style={{ background: '#0F1923', border: '1px solid #2D3748' }}
    >
      <div className="p-2">
        {groups.map((g, gi) => (
          <div key={g.name} className={gi > 0 ? 'mt-3' : ''}>
            <p
              className="px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] select-none"
              style={{ color: '#4A5568' }}
              data-testid={`settings-group-${g.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}
            >
              {g.name}
            </p>
            <ul className="space-y-0.5">
              {g.items.map((it) => {
                const Icon = it.icon;
                const isActive = !!it.section && active === it.section;
                return (
                  <li key={it.key}>
                    <button
                      type="button"
                      onClick={() => (it.section ? onSelect(it.section) : navigate(it.path))}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left transition-colors"
                      style={{
                        background: isActive ? 'rgba(30,136,229,0.12)' : 'transparent',
                        color: isActive ? '#1E88E5' : '#CBD5E1',
                      }}
                      data-testid={`settings-nav-${it.key}`}
                    >
                      <Icon
                        className="w-4 h-4 flex-shrink-0"
                        strokeWidth={isActive ? 2.5 : 1.75}
                        style={{ color: isActive ? '#1E88E5' : '#64748B' }}
                      />
                      <span className="flex-1 truncate">{it.label}</span>
                      <ChevronRight
                        className="w-4 h-4 flex-shrink-0 opacity-40"
                        style={{ color: isActive ? '#1E88E5' : '#64748B' }}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}


// Notification preferences card (Settings → Notifications)
function NotificationPrefsLauncher() {
  const [open, setOpen] = useState(true); // open immediately when the user lands on /settings?section=notifications

  return (
    <div className="rounded-xl p-6" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
      <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
        <Bell className="w-5 h-5" style={{ color: '#1E88E5' }} />
        Notification Preferences
      </h2>
      <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>
        Choose which Hines Time events ping the in-app bell and which ones email you. By default
        the bell is on for everything and email is off — so admins stop drowning in routine
        notifications.
      </p>
      <button
        onClick={() => setOpen(true)}
        className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        style={{ background: '#1E88E5', color: '#fff' }}
        data-testid="open-notification-prefs-dialog"
      >
        Manage preferences
      </button>
      <NotificationPreferencesDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}


function NotificationPrefsCard({ token }) {
  const { toast } = useToast();
  const API = `${window.__BACKEND_URL__}/api`;

  const [prefs, setPrefs] = useState(null); // null = loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/users/me/notification-prefs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (mounted) setPrefs(r.data?.notification_prefs || {});
      } catch (err) {
        if (mounted) {
          toast({ title: 'Error', description: 'Failed to load notification preferences', variant: 'destructive' });
          setPrefs({});
        }
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const togglePref = async (key, nextVal) => {
    const prev = prefs?.[key];
    // Optimistic update so the switch reacts instantly.
    setPrefs((p) => ({ ...p, [key]: nextVal }));
    setSaving(true);
    try {
      await axios.put(
        `${API}/users/me/notification-prefs`,
        { [key]: nextVal },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({ title: 'Saved', description: nextVal ? 'You\'ll receive these emails.' : 'You won\'t receive these emails.' });
    } catch (err) {
      // Roll back on failure
      setPrefs((p) => ({ ...p, [key]: prev }));
      toast({ title: 'Error', description: 'Failed to save preference', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const items = [
    {
      key: 'email_card_shared',
      title: 'Card shared with you',
      description: 'Get an email when a teammate shares a board card with you.',
    },
    {
      key: 'email_workplan_sent',
      title: 'Workplan sent to you',
      description: 'Get an email when a teammate sends or assigns a workplan to you.',
    },
  ];

  return (
    <div className="rounded-xl p-6" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
      <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
        <Bell className="w-5 h-5" style={{ color: '#1E88E5' }} />
        Email Notifications
      </h2>
      <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>
        Choose which Hines Time events should land in your inbox. In-app notifications are always delivered.
      </p>

      {prefs === null ? (
        <p className="text-sm" style={{ color: '#64748B' }}>Loading…</p>
      ) : (
        <ul className="space-y-3" data-testid="notification-prefs-list">
          {items.map((it) => {
            const on = prefs[it.key] !== false; // default ON
            return (
              <li
                key={it.key}
                className="rounded-lg p-4 flex items-start justify-between gap-4"
                style={{ background: '#1A2433', border: '1px solid #2D3748' }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">{it.title}</p>
                  <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{it.description}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  disabled={saving}
                  onClick={() => togglePref(it.key, !on)}
                  className="relative inline-flex items-center w-11 h-6 rounded-full transition-colors flex-shrink-0 disabled:opacity-60"
                  style={{ background: on ? '#1E88E5' : '#374151' }}
                  data-testid={`notification-pref-${it.key}`}
                >
                  <span
                    className="inline-block w-5 h-5 transform bg-white rounded-full transition-transform shadow-md"
                    style={{ transform: on ? 'translateX(22px)' : 'translateX(2px)' }}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// Weekly Digest Settings Component
function WeeklyDigestCard({ token, company }) {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(company?.weekly_digest_enabled !== false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const API = `${window.__BACKEND_URL__}/api`;

  const toggleDigest = async (newVal) => {
    try {
      await axios.put(`${API}/company/digest-settings`,
        { weekly_digest_enabled: newVal },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnabled(newVal);
      toast({ title: newVal ? 'Digest enabled' : 'Digest disabled', description: newVal ? 'You will receive weekly email summaries every Monday.' : 'Weekly digest emails have been turned off.' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update settings', variant: 'destructive' });
    }
  };

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/digest/preview`, { headers: { Authorization: `Bearer ${token}` } });
      setPreview(res.data.summary);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load preview', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/digest/send-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Digest sent', description: res.data.message });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to send digest', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const [smsTesting, setSmsTesting] = useState(false);
  const sendTestSms = async () => {
    setSmsTesting(true);
    try {
      const res = await axios.post(`${API}/admin/sms-test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Test text sent', description: `Check ${res.data.phone} — it should arrive within seconds.` });
    } catch (err) {
      toast({ title: 'Could not send test text', description: err.response?.data?.detail || 'Unknown error', variant: 'destructive' });
    } finally {
      setSmsTesting(false);
    }
  };

  return (
    <Card className="shadow-lg mb-6 border-2 border-[#2D3748]">
      <CardHeader className="bg-indigo-50 border-b border-[#2D3748]">
        <CardTitle className="text-lg font-bold text-indigo-900 flex items-center gap-2">
          <CalendarClock className="w-5 h-5" /> Weekly Email Digest
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <p className="text-sm text-slate-400">
          Receive a weekly summary of jobs, hours, and overdue items every Monday morning.
        </p>

        <div className="flex items-center justify-between p-3 bg-[#1A2433] rounded-lg">
          <div>
            <p className="text-sm font-medium text-white">Weekly digest emails</p>
            <p className="text-xs text-gray-500">Sent to all company admins on Mondays at 7 AM UTC</p>
          </div>
          <button
            onClick={() => toggleDigest(!enabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-[#818CF8]' : 'bg-gray-300'}`}
            data-testid="digest-toggle"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-[#1A2433] rounded-full transition-transform shadow ${enabled ? 'translate-x-6' : ''}`} />
          </button>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadPreview} disabled={loading} data-testid="digest-preview-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CalendarClock className="w-4 h-4 mr-1" />}
            Preview This Week
          </Button>
          <Button size="sm" onClick={sendNow} disabled={sending} className="bg-[#818CF8] hover:bg-indigo-700" data-testid="digest-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Send Now
          </Button>
          <Button variant="outline" size="sm" onClick={sendTestSms} disabled={smsTesting} data-testid="sms-test-btn">
            {smsTesting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Phone className="w-4 h-4 mr-1" />}
            Send me a test text
          </Button>
        </div>

        {preview && (
          <div className="p-4 bg-[#1A2433] border rounded-lg space-y-3 text-sm" data-testid="digest-preview">
            <p className="font-semibold text-white">Preview: Week of {preview.week_label}</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-[#0F1923] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-blue-700">{preview.total_jobs}</p>
                <p className="text-xs text-blue-500">Total Jobs</p>
              </div>
              <div className="bg-[#0F1923] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-green-700">{preview.completed_this_week}</p>
                <p className="text-xs text-green-500">Completed</p>
              </div>
              <div className="bg-[#0F1923] rounded-lg p-3 text-center">
                <p className="text-xl font-bold text-purple-700">{preview.total_hours}h</p>
                <p className="text-xs text-purple-500">Hours</p>
              </div>
            </div>
            {preview.overdue_jobs > 0 && (
              <div className="bg-[#0F1923] border border-[#2D3748] rounded-lg p-3">
                <p className="text-red-700 font-medium">{preview.overdue_jobs} overdue job{preview.overdue_jobs !== 1 ? 's' : ''}</p>
              </div>
            )}
            {preview.top_workers?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Top Workers</p>
                {preview.top_workers.map((w) => (
                  <div key={w.id || w.user_id || `${w.name}-${w.hours}`} className="flex justify-between py-1 text-xs">
                    <span className="text-slate-300">{w.name}</span>
                    <span className="font-medium text-white">{w.hours}h</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// QuickBooks Integration Card
function QuickBooksCard({ token }) {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  // Service-item editor
  const [serviceItemDraft, setServiceItemDraft] = useState('');
  const [savingServiceItem, setSavingServiceItem] = useState(false);

  const API = `${window.__BACKEND_URL__}/api`;

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, errorsRes] = await Promise.all([
        axios.get(`${API}/qb/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/qb/sync-errors`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStatus(statusRes.data);
      setServiceItemDraft(statusRes.data?.service_item_name || 'Facilities Services');
      setErrors(errorsRes.data);
    } catch (err) {
      console.error('Failed to load QuickBooks status', err);
    }
    finally { setLoading(false); }
  }, [API, token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const saveServiceItem = async () => {
    const name = (serviceItemDraft || '').trim();
    if (!name) {
      toast({ title: 'Name required', description: 'Service item name cannot be empty.', variant: 'destructive' });
      return;
    }
    setSavingServiceItem(true);
    try {
      const r = await axios.put(
        `${API}/qb/service-item`,
        { service_item_name: name },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: 'Service item saved',
        description: r.data?.message || 'The next invoice sync will use this item.',
      });
      // Re-fetch so the cached id is reflected.
      fetchStatus();
    } catch (err) {
      toast({
        title: 'Could not save',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSavingServiceItem(false);
    }
  };

  // Handle the post-OAuth redirect (?qb=connected | ?qb=error&msg=...).
  // Backend redirects to /settings?qb=... after the Intuit callback; without
  // this handler the user lands back here with zero feedback and assumes the
  // integration is broken. Production bug, fixed May 2026.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qb = params.get('qb');
    if (!qb) return;

    if (qb === 'connected') {
      toast({
        title: 'QuickBooks Connected ✅',
        description: 'Your QuickBooks Online account is now linked. Invoices and timesheets can be synced.',
      });
      fetchStatus(); // refresh the connected banner
    } else if (qb === 'error') {
      const msg = params.get('msg') || 'Unknown error. Please try again.';
      toast({
        title: 'QuickBooks Connection Failed',
        description: msg,
        variant: 'destructive',
      });
    }
    // Clean the URL so the toast doesn't fire again on refresh
    params.delete('qb');
    params.delete('msg');
    const rest = params.toString();
    const cleaned = window.location.pathname + (rest ? `?${rest}` : '');
    window.history.replaceState(null, '', cleaned);
  }, [fetchStatus, toast]);

  const handleConnect = async () => {
    if (connecting) return;
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/qb/connect`, { params: { origin: window.location.origin }, headers: { Authorization: `Bearer ${token}` } });
      if (!res.data?.auth_url) {
        throw new Error('Server returned an empty auth URL');
      }
      // Show a visible "redirecting" toast in case the redirect is slow or
      // gets popup-blocked.
      toast({
        title: 'Redirecting to QuickBooks…',
        description: 'You should see Intuit\'s sign-in page in a moment.',
      });
      // Tiny delay so the toast paints before navigation.
      setTimeout(() => {
        window.location.href = res.data.auth_url;
      }, 400);
    } catch (err) {
      console.error('QB connect failed', err);
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      toast({
        title: 'Could not start QuickBooks connection',
        description: detail,
        variant: 'destructive',
      });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect QuickBooks? This will stop all syncing.')) return;
    try {
      await axios.post(`${API}/qb/disconnect`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setStatus({ connected: false });
      toast({ title: 'Disconnected', description: 'QuickBooks has been disconnected.' });
    } catch (err) {
      console.error('QuickBooks disconnect failed', err);
      toast({ title: 'Error', description: 'Could not disconnect QuickBooks. Please try again.', variant: 'destructive' });
    }
  };

  return (
    <Card className="shadow-lg mb-6 border-2 border-[#2D3748]" style={{ background: '#1A2433' }}>
      <CardHeader style={{ borderBottom: '1px solid #2D3748' }}>
        <CardTitle className="text-lg font-bold text-white flex items-center gap-2">
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#2CA01C"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-6h2v6zm4 0h-2v-6h2v6z"/></svg>
          QuickBooks Online
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        {loading ? (
          <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" style={{ color: '#1E88E5' }} /><span style={{ color: '#A0AEC0' }}>Checking connection...</span></div>
        ) : status?.connected ? (
          <div>
            {status.needs_reconnect && (
              <div className="flex items-start gap-3 p-4 rounded-xl mb-3" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)' }} data-testid="qb-reconnect-banner">
                <RefreshCw className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: '#F59E0B' }}>QuickBooks session expired — reconnect to resume syncing</p>
                  <p className="text-xs mt-0.5" style={{ color: '#A0AEC0' }}>
                    QuickBooks stopped accepting our saved credentials{status.token_error ? ` (${status.token_error.slice(0, 120)})` : ''}. Invoice and payroll pushes will fail until you reconnect. Nothing already synced is affected.
                  </p>
                  <button onClick={handleConnect} disabled={connecting} className="mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: '#F59E0B' }} data-testid="qb-reconnect-btn">
                    {connecting ? 'Redirecting…' : 'Reconnect QuickBooks'}
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(0,200,150,0.1)', border: '1px solid rgba(0,200,150,0.25)' }} data-testid="qb-connected-banner">
              <div className="w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center mt-0.5" style={{ background: 'rgba(0,200,150,0.2)' }}>
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="#00C896"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: '#00C896' }} data-testid="qb-connected-label">
                  Connected to QuickBooks Online
                </p>
                {status.company_name ? (
                  <p className="text-base font-bold text-white truncate mt-0.5" data-testid="qb-company-name" title={status.legal_name || status.company_name}>
                    {status.company_name}
                  </p>
                ) : (
                  <p className="text-xs italic mt-0.5" style={{ color: '#A0AEC0' }}>
                    Fetching QuickBooks company name…
                  </p>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px]" style={{ color: '#64748B' }}>
                  {status.country && (
                    <span data-testid="qb-country">{status.country}</span>
                  )}
                  {status.realm_id && (
                    <span data-testid="qb-realm-id">Realm {status.realm_id.substring(0, 10)}…</span>
                  )}
                  {status.connected_at && (
                    <span data-testid="qb-connected-since">
                      Connected {new Date(status.connected_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button onClick={handleDisconnect} className="mt-3 text-xs px-3 py-1.5 rounded-lg" style={{ color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' }} data-testid="qb-disconnect">
              Disconnect
            </button>

            {/* Service item — the QB Product/Service every invoice line uses.
                Required by QuickBooks; missing this is the #1 cause of
                "Error 6000: Business Validation Error" on sync. */}
            <div
              className="mt-4 p-4 rounded-xl"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
              data-testid="qb-service-item-section"
            >
              <h4 className="text-sm font-semibold text-white mb-1">Invoice service item</h4>
              <p className="text-xs mb-3" style={{ color: '#94A3B8' }}>
                Every invoice line in QuickBooks must reference a Product/Service. We'll auto-create one
                with this name if it doesn't exist in your QuickBooks file.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={serviceItemDraft}
                  onChange={(e) => setServiceItemDraft(e.target.value)}
                  placeholder="e.g. Facilities Services"
                  className="flex-1 px-3 py-2 rounded-lg text-sm bg-[#1A2433] border border-[#2D3748] text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                  data-testid="qb-service-item-input"
                  maxLength={100}
                />
                <button
                  onClick={saveServiceItem}
                  disabled={savingServiceItem || serviceItemDraft === (status?.service_item_name || '')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  style={{ background: '#1E88E5', color: '#fff' }}
                  data-testid="qb-service-item-save"
                >
                  {savingServiceItem ? 'Saving…' : 'Save'}
                </button>
              </div>
              {status?.service_item_id && (
                <p className="text-[11px] mt-2" style={{ color: '#64748B' }}>
                  Linked to QuickBooks item id <span className="font-mono">{status.service_item_id}</span>.
                </p>
              )}
            </div>
            <QbConnectionTest token={token} />
            <PayrollReadinessCheck token={token} />
          </div>
        ) : (
          <div>
            <p className="text-sm mb-3" style={{ color: '#A0AEC0' }}>
              Connect QuickBooks to sync invoices and push timesheets for payroll.
            </p>
            {status?.environment && (
              <div
                className="mb-3 text-[11px] flex items-center gap-1.5 px-2 py-1 rounded-md inline-flex"
                style={{
                  background: status.environment === 'production' ? 'rgba(46,160,67,0.10)' : 'rgba(245,158,11,0.10)',
                  color: status.environment === 'production' ? '#86EFAC' : '#FCD34D',
                  border: `1px solid ${status.environment === 'production' ? 'rgba(46,160,67,0.3)' : 'rgba(245,158,11,0.3)'}`,
                }}
                data-testid="qb-env-indicator"
                title={
                  status.environment === 'production'
                    ? 'Backend is configured for the production Intuit API. Your QB app must have production keys.'
                    : 'Backend is in sandbox mode — use a QuickBooks sandbox company, not your real one.'
                }
              >
                Backend mode: <strong style={{ marginLeft: 2 }}>{status.environment}</strong>
              </div>
            )}
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="px-4 py-2.5 rounded-xl text-sm font-medium text-white ht-btn-glow disabled:opacity-60 disabled:cursor-wait"
              style={{ background: '#2CA01C' }}
              data-testid="qb-connect"
            >
              {connecting ? 'Redirecting to QuickBooks…' : 'Connect to QuickBooks'}
            </button>
            <PayrollReadinessCheck token={token} />
          </div>
        )}

        <PayItemsMapping token={token} />
        <AutoLockSwitch token={token} />
        <QbSyncErrors token={token} errors={errors} setErrors={setErrors} connected={!!status?.connected && !status?.needs_reconnect} toast={toast} />
      </CardContent>
    </Card>
  );
}
